
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, Users, DollarSign, Percent, Globe } from "lucide-react";

interface GlobalMetricsProps {
  metrics: {
    totalAum: number;
    totalFollowers: number;
    averagePnl: number;
    strategyCount: number;
    exchangeCount: number;
  };
}

const GlobalMetricsCard = ({ metrics }: GlobalMetricsProps) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  const formatPercent = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };

  return (
    <Card className="border-t-4 border-t-blue-600">
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <Globe className="mr-2 h-5 w-5 text-blue-600" />
          Global Performance Metrics
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Total AUM</span>
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 text-green-600 mr-1" />
              <span className="text-2xl font-bold">{formatCurrency(metrics.totalAum)}</span>
            </div>
          </div>
          
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Total Followers</span>
            <div className="flex items-center">
              <Users className="h-4 w-4 text-blue-600 mr-1" />
              <span className="text-2xl font-bold">{metrics.totalFollowers.toLocaleString()}</span>
            </div>
          </div>
          
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Avg P/L</span>
            <div className="flex items-center">
              <Percent className="h-4 w-4 text-orange-600 mr-1" />
              <span className={`text-2xl font-bold ${metrics.averagePnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatPercent(metrics.averagePnl)}
              </span>
            </div>
          </div>
          
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Strategies</span>
            <div className="flex items-center">
              <BarChart3 className="h-4 w-4 text-purple-600 mr-1" />
              <span className="text-2xl font-bold">{metrics.strategyCount}</span>
            </div>
          </div>
          
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Exchanges</span>
            <div className="flex items-center">
              <Globe className="h-4 w-4 text-cyan-600 mr-1" />
              <span className="text-2xl font-bold">{metrics.exchangeCount}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GlobalMetricsCard;
